import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { collection, getDocs, query, orderBy } from 'firebase/firestore';
import { db, isFirebaseConfigured } from '../../firebase';
import './PageFeedbackNotes.css';

const STORAGE_KEY = 'lms-page-feedback-notes-visible';
const TYPE_LABELS = { suggestion: 'Suggestion', bug: 'Bug', general: 'General' };

function formatDate(isoString) {
  if (!isoString) return '';
  try {
    const d = new Date(isoString);
    return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
  } catch {
    return '';
  }
}

/** Returns whether this feedback item was submitted on the current page (by path). */
function matchesPage(feedback, pathname) {
  const url = feedback.pageUrl || '';
  if (!url) return false;
  try {
    const u = new URL(url);
    return u.pathname === pathname;
  } catch {
    return url.includes(pathname);
  }
}

export default function PageFeedbackNotes() {
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(() => {
    try {
      return localStorage.getItem(STORAGE_KEY) === 'true';
    } catch {
      return false;
    }
  });
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!isOpen) {
      setItems([]);
      setError(null);
      return;
    }
    let cancelled = false;
    const firebaseReady = isFirebaseConfigured() && db;
    if (!firebaseReady) {
      setError('Feedback not configured');
      return;
    }
    setLoading(true);
    setError(null);
    (async () => {
      try {
        const q = query(collection(db, 'feedback'), orderBy('createdAt', 'desc'));
        const snap = await getDocs(q);
        if (cancelled) return;
        const list = snap.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
        const pathname = location.pathname || '/';
        const forPage = list.filter((item) => matchesPage(item, pathname));
        setItems(forPage);
      } catch (err) {
        if (!cancelled) setError(err.message || 'Could not load feedback');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [isOpen, location.pathname]);

  const toggleOpen = () => {
    const next = !isOpen;
    setIsOpen(next);
    try {
      localStorage.setItem(STORAGE_KEY, next ? 'true' : 'false');
    } catch (_) {}
  };

  return (
    <>
      {/* <button
        type="button"
        className="page-feedback-notes-toggle  "
        onClick={toggleOpen}
        aria-expanded={isOpen}
        aria-label={isOpen ? 'Hide feedback for this page' : 'Show feedback for this page'}
        title={isOpen ? 'Hide page feedback' : 'Show page feedback'}
      >
        <span className="page-feedback-notes-toggle-icon" aria-hidden="true">📌</span>
        <span className="page-feedback-notes-toggle-label">Page feedback</span>
      </button> */}

      {isOpen && (
        <div className="page-feedback-notes-panel" role="region" aria-label="Feedback for this page">
          <div className="page-feedback-notes-header">
            <h3 className="page-feedback-notes-title">Feedback on this page</h3>
            <button
              type="button"
              className="page-feedback-notes-close"
              onClick={toggleOpen}
              aria-label="Close"
            >
              ×
            </button>
          </div>
          <div className="page-feedback-notes-body">
            {loading && <p className="page-feedback-notes-message">Loading…</p>}
            {error && <p className="page-feedback-notes-error">{error}</p>}
            {!loading && !error && items.length === 0 && (
              <p className="page-feedback-notes-message">No feedback submitted on this page yet.</p>
            )}
            {!loading && !error && items.length > 0 && (
              <ul className="page-feedback-notes-list">
                {items.map((item) => (
                  <li key={item.id} className={`page-feedback-note page-feedback-note--${item.type || 'general'}`}>
                    <span className="page-feedback-note-type">
                      {TYPE_LABELS[item.type] || 'General'}
                    </span>
                    <p className="page-feedback-note-message">{item.message}</p>
                    <div className="page-feedback-note-meta">
                      {item.email && <span className="page-feedback-note-email">{item.email}</span>}
                      <time dateTime={item.createdAt}>{formatDate(item.createdAt)}</time>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}
    </>
  );
}
